import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Formik } from "formik";
import { Form, Col, Modal, Row } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import * as yup from "yup";
import { acceptInvitation } from "actions/userActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { userConstants } from "actions/types";
import {
  invitationCodeParser,
  noWhitespaceRegex,
  validIconInvite,
  whiteSpaceError,
} from "utils/fieldValidators";

import Button from "components/Button";
import Alert from "components/Alert";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import type { RootState } from "types/state";
import type { EmployerLink } from "./contributionTypes";

const schema = yup.object({
  invitationCode: yup
    .string()
    .label("Invitation code")
    .required()
    .min(7)
    .max(10)
    .matches(noWhitespaceRegex, whiteSpaceError)
    .matches(validIconInvite),
});

const actions = [
  userConstants.USER_EMPLOYER_LINK,
  userConstants.USER_ACCEPT_INVITATION,
];

const isFetchingSelector = createLoadingSelector(actions);
const errorSelector = createErrorSelector(actions);

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isFetchingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  acceptInvitation,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  setEmployerLink: (employerLink: EmployerLink) => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  invitationCode: string;
}

interface AcceptInviteFormValues {
  invitationCode: string;
}

class AcceptInvite extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      invitationCode: "",
    };
  }

  _linkEmployer = (values: AcceptInviteFormValues) => {
    const invitationCode = values.invitationCode;
    void this.props
      .acceptInvitation(
        this.props.client as ApolloClient<object>,
        invitationCode
      )
      .then((res) => {
        if (!this.props.error) {
          // acceptInvitation resolves to `void | EmployerLink | null` (the
          // thunk returns nothing on its error branch). The original passed
          // `res` straight through; cast to the shared EmployerLink to preserve
          // that behavior (downstream tolerates null/undefined via isEmpty).
          this.props.setEmployerLink(res as EmployerLink);
        }
      });
  };

  render() {
    return (
      <>
        <Modal.Header closeButton>
          <IconHeader variant="cardHeader" headerText="Employer Plan" />
          <IconSubheader
            variant="subheading"
            subheader="In order to link with your employer please provide your Plan ID or invitation code you should have received in an email from your employer."
          />
        </Modal.Header>
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          enableReinitialize={true}
          onSubmit={(values) => {
            this._linkEmployer(values);
          }}
          initialValues={{
            invitationCode: this.state.invitationCode,
          }}
        >
          {({
            setFieldValue,
            handleSubmit,
            handleBlur,
            values,
            touched,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Modal.Body>
                <Row className="mb-3">
                  <Form.Group
                    as={Col}
                    md={6}
                    controlId="formBasicInvitationCode"
                  >
                    <Form.Label>Employer Plan ID</Form.Label>
                    <Form.Control
                      name="invitationCode"
                      placeholder="Plan ID"
                      value={values.invitationCode}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        const value = invitationCodeParser(
                          event,
                          // PRESERVED BUG (conversion-only): invitationCodeParser
                          // takes a single argument; this second argument has
                          // always been silently ignored at runtime.
                          // @ts-expect-error preserved extra argument (no-op at runtime)
                          values.invitationCode
                        );
                        void setFieldValue("invitationCode", value);
                      }}
                      isInvalid={
                        touched.invitationCode && !!errors.invitationCode
                      }
                      isValid={touched.invitationCode && !errors.invitationCode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.invitationCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
                {this.props.error && (
                  <Alert type="error" msg={this.props.error} />
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  type="submit"
                  name="submit"
                  withArrow={true}
                  btnLabel="Continue"
                  disabled={!values.invitationCode}
                  loading={this.props.isFetching}
                />
              </Modal.Footer>
            </Form>
          )}
        </Formik>
      </>
    );
  }
}

export default connector(withApollo<Props>(AcceptInvite));
